import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Paciente, CreatePacienteData } from "@/hooks/usePacientes";
import { formatAddressFromCep, lookupCep, maskCep, maskCpf, maskPhone } from "@/lib/brasil";
import { toast } from "sonner";
import { useAutomaticBrazilLookup } from "@/hooks/useAutomaticBrazilLookup";

interface PacienteFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePacienteData) => Promise<void>;
  paciente?: Paciente | null;
  title: string;
}

export function PacienteForm({ isOpen, onClose, onSubmit, paciente, title }: PacienteFormProps) {
  const [formData, setFormData] = useState<CreatePacienteData>({
    nome: "",
    email: "",
    telefone: "",
    data_nascimento: "",
    ultima_consulta: "",
    status: "Ativo",
    endereco: "",
    cep: "",
    cpf: "",
    autoriza_email: true,
    autoriza_whatsapp: true,
    autoriza_marketing: false,
    optout_email: false,
    optout_whatsapp: false,
  });
  const [loading, setLoading] = useState(false);
  const [autoCepEnabled, setAutoCepEnabled] = useState(false);

  useEffect(() => {
    setFormData({
      nome: paciente?.nome || "",
      email: paciente?.email || "",
      telefone: paciente?.telefone || "",
      data_nascimento: paciente?.data_nascimento || "",
      ultima_consulta: paciente?.ultima_consulta || "",
      status: paciente?.status || "Ativo",
      endereco: paciente?.endereco || "",
      cep: paciente?.cep || "",
      cpf: paciente?.cpf || "",
      autoriza_email: paciente?.autoriza_email !== false,
      autoriza_whatsapp: paciente?.autoriza_whatsapp !== false,
      autoriza_marketing: !!paciente?.autoriza_marketing,
      optout_email: !!paciente?.optout_email,
      optout_whatsapp: !!paciente?.optout_whatsapp,
    });
    setAutoCepEnabled(false);
  }, [paciente]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CreatePacienteData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const cepLookup = useAutomaticBrazilLookup({
    value: formData.cep || "",
    digits: 8,
    enabled: autoCepEnabled,
    lookup: lookupCep,
    onSuccess: (info) => {
      setFormData((prev) => ({
        ...prev,
        cep: info.cep,
        endereco: prev.endereco || formatAddressFromCep(info),
      }));
      toast.success("CEP encontrado");
    },
    onError: (error) => {
      toast.error(error.message || "Nao foi possivel buscar o CEP");
    },
  });

  const handleCepLookup = () => void cepLookup.run();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input id="nome" value={formData.nome} onChange={(e) => handleInputChange("nome", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cpf">CPF</Label>
              <Input id="cpf" value={formData.cpf} onChange={(e) => handleInputChange("cpf", maskCpf(e.target.value))} placeholder="000.000.000-00" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone *</Label>
              <Input id="telefone" value={formData.telefone} onChange={(e) => handleInputChange("telefone", maskPhone(e.target.value))} placeholder="(11) 99999-9999" required />
            </div>
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-3">
            <div className="space-y-2">
              <Label htmlFor="cep">CEP</Label>
              <Input id="cep" value={formData.cep || ""} onChange={(e) => { setAutoCepEnabled(true); handleInputChange("cep", maskCep(e.target.value)); }} placeholder="00000-000" />
              {cepLookup.loading && <p className="text-xs text-muted-foreground">Consultando CEP...</p>}
            </div>
            <div className="flex items-end">
              <Button type="button" variant="outline" onClick={handleCepLookup} disabled={cepLookup.loading || !formData.cep}>
                {cepLookup.loading ? "Buscando..." : "Consultar novamente"}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="data_nascimento">Data de Nascimento</Label>
              <Input id="data_nascimento" type="date" value={formData.data_nascimento} onChange={(e) => handleInputChange("data_nascimento", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ultima_consulta">Ultima Consulta</Label>
              <Input id="ultima_consulta" type="date" value={formData.ultima_consulta} onChange={(e) => handleInputChange("ultima_consulta", e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="endereco">Endereco</Label>
            <Textarea id="endereco" value={formData.endereco} onChange={(e) => handleInputChange("endereco", e.target.value)} rows={2} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status} onValueChange={(value: "Ativo" | "Inativo") => handleInputChange("status", value)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Ativo">Ativo</SelectItem>
                <SelectItem value="Inativo">Inativo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3 rounded-md border p-4">
            <div><p className="text-sm font-medium">Preferências de comunicação</p><p className="text-xs text-muted-foreground">Marketing só será enviado quando houver autorização explícita.</p></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-center justify-between gap-3 text-sm"><span>Comunicações por e-mail</span><Switch checked={formData.autoriza_email !== false && !formData.optout_email} onCheckedChange={(checked) => { handleInputChange("autoriza_email", checked); handleInputChange("optout_email", !checked); }} /></label>
              <label className="flex items-center justify-between gap-3 text-sm"><span>Comunicações por WhatsApp</span><Switch checked={formData.autoriza_whatsapp !== false && !formData.optout_whatsapp} onCheckedChange={(checked) => { handleInputChange("autoriza_whatsapp", checked); handleInputChange("optout_whatsapp", !checked); }} /></label>
              <label className="flex items-center justify-between gap-3 text-sm sm:col-span-2"><span>Autoriza campanhas de marketing</span><Switch checked={!!formData.autoriza_marketing} onCheckedChange={(checked) => handleInputChange("autoriza_marketing", checked)} /></label>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={loading}>{loading ? "Salvando..." : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
